import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import Button from '../../components/common/button';
import Chip from '../../components/common/chip';
import {
  Autocomplete,
  Box,
  Container,
  Grid,
  InputAdornment,
  TextField,
  Slider,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Stack,
} from '@mui/material';
import { LANGUAGES } from '../../utils/languages';
import { MINORITY_GROUPS, TOPICS } from '../../utils/groups';
import {
  ACCOUNT_TYPES,
  PRODUCT_TYPES,
  PRODUCT_TYPES_FRIENDLY,
} from '../../constants';
import SearchIcon from '../../assets/icons/Search.svg';
import { Filters, FilterField, FilterTitle } from './experts.style';
import { SET_FILTERS, RESET_FILTERS } from '../../store/actions';
import { INSURANCES } from '../../utils/insurance';
import { modifyReimbursementLabels } from '../../components/product/utils/format-reimbursement';

function Filter({ onApply, onClose }) {
  const dispatcher = useDispatch();

  const user = useSelector((state) => state.account.user);

  const keyTerm = useSelector((state) => state.search.filters.keyTerm);
  const languages = useSelector((state) => state.search.filters.languages);
  const minorityGroups = useSelector(
    (state) => state.search.filters.minorityGroups,
  );
  const topics = useSelector((state) => state.search.filters.topics);
  const productTypes = useSelector(
    (state) => state.search.filters.productTypes,
  );
  const priceRange = useSelector((state) => state.search.filters.priceRange);
  const virtual = useSelector((state) => state.search.filters.virtual);
  const inPerson = useSelector((state) => state.search.filters.inPerson);
  const radius = useSelector((state) => state.search.filters.radius);
  const zip = useSelector((state) => state.search.filters.zip);
  const insurances = useSelector((state) => state.search.filters.insurances);

  const [expandFilters, setExpandFilters] = useState(false);

  const toggleMoreFilters = () => {
    setExpandFilters(!expandFilters);
  };

  const setFilters = (filters) => {
    dispatcher({
      type: SET_FILTERS,
      payload: filters,
    });

    onApply();
  };

  const resetFilters = () => {
    dispatcher({
      type: RESET_FILTERS,
      payload: {},
    });

    onApply();
  };

  return (
    <Container maxWidth="md">
      <Filters>
        <Grid container spacing={4}>
          <Grid item xs={12}>
            <FilterTitle>Filters</FilterTitle>
          </Grid>
          <Grid item xs={12}>
            <TextField
              id="search-key-term"
              placeholder="Keyword..."
              variant="outlined"
              onChange={(e) => setFilters({ keyTerm: e.target.value })}
              value={keyTerm}
              size="small"
              sx={{ width: '100%' }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <img
                      src={SearchIcon}
                      alt="search"
                      width={20}
                      style={{ marginLeft: -5 }}
                    />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <FilterField>Topics:</FilterField>
            <Grid container spacing={0}>
              {TOPICS.map((topic) => (
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    key={topic}
                    control={
                      <Checkbox
                        checked={topics.includes(topic)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setFilters({ topics: [...topics, topic] });
                          } else {
                            setFilters({
                              topics: topics.filter((t) => t !== topic),
                            });
                          }
                        }}
                      />
                    }
                    label={topic}
                  />
                </Grid>
              ))}
              {user && user.profile.profile_type === ACCOUNT_TYPES.EXPERT && (
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    key="Experts for Experts"
                    control={
                      <Checkbox
                        checked={topics.includes('Experts for Experts')}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setFilters({
                              topics: [...topics, 'Experts for Experts'],
                            });
                          } else {
                            setFilters({
                              topics: topics.filter(
                                (t) => t !== ['Experts for Experts'],
                              ),
                            });
                          }
                        }}
                      />
                    }
                    label="Experts for Experts"
                  />
                </Grid>
              )}
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <FilterField>Service Types:</FilterField>
            <Grid container spacing={0}>
              {Object.keys(PRODUCT_TYPES).map((key) => (
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    key={PRODUCT_TYPES[key]}
                    control={
                      <Checkbox
                        checked={productTypes.includes(PRODUCT_TYPES[key])}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setFilters({
                              productTypes: [
                                ...productTypes,
                                PRODUCT_TYPES[key],
                              ],
                            });
                          } else {
                            setFilters({
                              productTypes: productTypes.filter(
                                (t) => t !== PRODUCT_TYPES[key],
                              ),
                            });
                          }
                        }}
                      />
                    }
                    label={PRODUCT_TYPES_FRIENDLY[key]}
                  />
                </Grid>
              ))}
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <FilterField>Price Range:</FilterField>
            <Box ml={1} width={'50%'}>
              <Slider
                getAriaLabel={() => 'Price range'}
                value={priceRange}
                onChange={(e, val) => setFilters({ priceRange: val })}
                step={10}
                min={0}
                max={1000}
                disableSwap
                marks={[
                  {
                    value: 0,
                    label: '$0',
                  },
                  {
                    value: 1000,
                    label: '$1,000+',
                  },
                ]}
                sx={{
                  '.MuiSlider-thumb': {
                    color: '#F08181',
                  },
                  '.MuiSlider-rail': {
                    color: '#F08181',
                  },
                  '.MuiSlider-track': {
                    color: '#F08181',
                  },
                }}
              />
            </Box>
            <Stack mt={1} direction="row" spacing={2}>
              <TextField
                id="min-price"
                variant="outlined"
                value={priceRange[0]}
                onChange={(e) =>
                  setFilters({ priceRange: [e.target.value, priceRange[1]] })
                }
                label="Min price"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">$</InputAdornment>
                  ),
                }}
              />
              <TextField
                id="max-price"
                variant="outlined"
                value={priceRange[1]}
                onChange={(e) =>
                  setFilters({ priceRange: [priceRange[0], e.target.value] })
                }
                label="Max price"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">$</InputAdornment>
                  ),
                }}
              />
            </Stack>
          </Grid>
          <Grid item xs={12}>
            <Button
              variant="text"
              onClick={toggleMoreFilters}
              style={{
                color: '#000000',
                textTransform: 'none',
                textDecoration: 'underline',
              }}
            >
              {expandFilters ? 'Show Less' : 'More Filters'}
            </Button>
          </Grid>
          {expandFilters && (
            <>
              <Grid item xs={12}>
                <FilterField>Languages Spoken:</FilterField>
                <Autocomplete
                  multiple
                  id="tags-filled"
                  options={LANGUAGES.map((option) => option)}
                  value={languages}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        key={option}
                        color="primaryLight"
                        label={option}
                        tagProps={{ ...getTagProps({ index }) }}
                      />
                    ))
                  }
                  sx={{
                    width: '100%',
                    mt: 2,
                  }}
                  renderInput={(params) => (
                    <TextField {...params} variant="outlined" size="small" />
                  )}
                  onChange={(event, newValue) => {
                    setFilters({ languages: newValue });
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <FilterField>Experienced with these groups:</FilterField>
                <Autocomplete
                  multiple
                  id="minority-identification"
                  options={MINORITY_GROUPS.map((option) => option)}
                  value={minorityGroups}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        key={option}
                        color="primaryLight"
                        label={option}
                        tagProps={{ ...getTagProps({ index }) }}
                      />
                    ))
                  }
                  sx={{
                    width: '100%',
                    mt: 2,
                  }}
                  renderInput={(params) => (
                    <TextField {...params} variant="outlined" size="small" />
                  )}
                  onChange={(event, newValue) => {
                    setFilters({ minorityGroups: newValue });
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <FilterField>Location:</FilterField>
                <Box sx={{ mt: 1 }}>
                  <FormGroup>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!!virtual}
                          onChange={(e) =>
                            setFilters({ virtual: e.target.checked })
                          }
                          sx={{
                            '&.Mui-checked': {
                              color: '#F08181',
                            },
                          }}
                        />
                      }
                      label="Virtual"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!!inPerson}
                          onChange={(e) =>
                            setFilters({ inPerson: e.target.checked })
                          }
                          sx={{
                            '&.Mui-checked': {
                              color: '#F08181',
                            },
                          }}
                        />
                      }
                      label="In Person"
                    />
                    {inPerson && (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          fontWeight: 400,
                          pl: 4,
                        }}
                      >
                        <span>within</span>
                        <TextField
                          id="radius"
                          placeholder="5"
                          variant="outlined"
                          onChange={(e) =>
                            setFilters({ radius: e.target.value })
                          }
                          value={radius}
                          size="small"
                          InputProps={{
                            sx: { ml: 1, mr: 1, width: 55, fontSize: 14 },
                          }}
                        />
                        <span>miles of</span>
                        <TextField
                          id="zip"
                          placeholder="12345"
                          variant="outlined"
                          onChange={(e) => setFilters({ zip: e.target.value })}
                          value={zip}
                          size="small"
                          InputProps={{
                            sx: { ml: 1, mr: 1, width: 70, fontSize: 14 },
                          }}
                        />
                      </Box>
                    )}
                  </FormGroup>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <FilterField>Insurance Options:</FilterField>
                <Grid container spacing={0}>
                  {INSURANCES.map((insurance) => (
                    <Grid item xs={12} sm={6}>
                      <FormControlLabel
                        key={insurance}
                        control={
                          <Checkbox
                            checked={insurances.includes(insurance)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setFilters({
                                  insurances: [...insurances, insurance],
                                });
                              } else {
                                setFilters({
                                  insurances: insurances.filter(
                                    (t) => t !== insurance,
                                  ),
                                });
                              }
                            }}
                          />
                        }
                        label={modifyReimbursementLabels([insurance])}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            </>
          )}
        </Grid>
        <Box
          sx={{
            mt: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-around',
          }}
        >
          <Box sx={{ ml: 1 }}>
            <Button shade="secondaryLight" onClick={resetFilters} size="large">
              Reset
            </Button>
          </Box>
          <Box sx={{ ml: 1 }}>
            <Button shade="secondary" onClick={onClose} size="large">
              See Results
            </Button>
          </Box>
        </Box>
      </Filters>
    </Container>
  );
}

export default Filter;
